/**
 * Mega Menu Controller
 * Self-contained vanilla JS. No dependencies on theme.js.
 * Handles hover/click full-width mega menu overlays on desktop.
 */
(function () {
  'use strict';

  if (window.MegaMenuController) return;

  // Simple debounce utility
  function debounce(fn, delay) {
    var timer;
    return function () {
      var context = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(context, args); }, delay);
    };
  }

  /**
   * MegaMenuDisclosure - handles a single trigger + overlay pair
   */
  class MegaMenuDisclosure {
    constructor(trigger, overlay, settings) {
      this.trigger = trigger;
      this.overlay = overlay;
      this.settings = settings;
      this.isOpen = false;
      this.hoverTimer = null;
      this.closeTimer = null;

      this._init();
    }

    _init() {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-haspopup', 'true');

      if (this.settings.triggerMode === 'click') {
        this.trigger.addEventListener('click', this._onClick.bind(this));
      } else {
        this.trigger.addEventListener('mouseenter', this._onMouseEnter.bind(this));
        this.trigger.addEventListener('mouseleave', this._onMouseLeave.bind(this));
        this.overlay.addEventListener('mouseenter', this._onOverlayEnter.bind(this));
        this.overlay.addEventListener('mouseleave', this._onOverlayLeave.bind(this));
      }

      this.trigger.addEventListener('keydown', this._onKeyDown.bind(this));
      document.addEventListener('keydown', this._onGlobalEscape.bind(this));
      document.addEventListener('click', this._onOutsideClick.bind(this));

      if (this.settings.closeOnScroll) {
        window.addEventListener('scroll', this._onScroll.bind(this), { passive: true });
      }

      window.addEventListener('resize', debounce(this._positionOverlay.bind(this), 100));
    }

    _onMouseEnter() {
      this._clearCloseTimer();
      var self = this;
      if (window.MegaMenuController.activeDisclosure && window.MegaMenuController.activeDisclosure !== this) {
        window.MegaMenuController.activeDisclosure.close(true);
        this.open();
      } else {
        this.hoverTimer = setTimeout(function () { self.open(); }, this.settings.hoverDelay || 200);
      }
    }

    _onMouseLeave() {
      this._clearHoverTimer();
      this._scheduleClose();
    }

    _onOverlayEnter() {
      this._clearCloseTimer();
    }

    _onOverlayLeave() {
      this._scheduleClose();
    }

    _onClick(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (this.isOpen) {
        this.close();
      } else {
        if (window.MegaMenuController.activeDisclosure && window.MegaMenuController.activeDisclosure !== this) {
          window.MegaMenuController.activeDisclosure.close(true);
        }
        this.open();
      }
    }

    _onKeyDown(evt) {
      if (evt.key === 'Enter' || evt.key === ' ') {
        evt.preventDefault();
        if (this.isOpen) {
          this.close();
          this.trigger.focus();
        } else {
          if (window.MegaMenuController.activeDisclosure && window.MegaMenuController.activeDisclosure !== this) {
            window.MegaMenuController.activeDisclosure.close(true);
          }
          this.open();
          var firstLink = this.overlay.querySelector('a');
          if (firstLink) firstLink.focus();
        }
      }
    }

    _onGlobalEscape(evt) {
      if (evt.key === 'Escape' && this.isOpen) {
        this.close();
        this.trigger.focus();
      }
    }

    _onOutsideClick(evt) {
      if (!this.isOpen) return;
      if (!this.trigger.contains(evt.target) && !this.overlay.contains(evt.target)) {
        this.close();
      }
    }

    _onScroll() {
      if (this.isOpen) this.close(true);
    }

    _scheduleClose() {
      this._clearCloseTimer();
      var self = this;
      this.closeTimer = setTimeout(function () { self.close(); }, 100);
    }

    _clearHoverTimer() {
      if (this.hoverTimer) { clearTimeout(this.hoverTimer); this.hoverTimer = null; }
    }

    _clearCloseTimer() {
      if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
    }

    open() {
      if (this.isOpen) return;
      this._clearHoverTimer();
      this._clearCloseTimer();
      this.isOpen = true;

      this.trigger.classList.add('is-mm-active');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.overlay.classList.add('is-active');
      this._positionOverlay();

      var isScrolled = window.scrollY > 10 || window.pageYOffset > 10;
      if (this.settings.headerSolidOnOpen && !isScrolled) {
        var wrapper = document.querySelector('.header-wrapper') || document.querySelector('#HeaderWrapper');
        if (wrapper) wrapper.classList.add('is-mm-header-solid');
      }

      document.dispatchEvent(new CustomEvent('megamenu:open', {
        detail: { trigger: this.trigger, overlay: this.overlay }
      }));

      window.MegaMenuController.activeDisclosure = this;
    }

    close(immediate) {
      if (!this.isOpen) return;
      this._clearHoverTimer();
      this._clearCloseTimer();
      this.isOpen = false;

      this.trigger.classList.remove('is-mm-active');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.overlay.classList.remove('is-active');

      var wrapper = document.querySelector('.header-wrapper') || document.querySelector('#HeaderWrapper');
      if (wrapper) wrapper.classList.remove('is-mm-header-solid');

      document.dispatchEvent(new CustomEvent('megamenu:close', {
        detail: { trigger: this.trigger, overlay: this.overlay }
      }));

      window.MegaMenuController.activeDisclosure = null;
    }

    _positionOverlay() {
      var header = document.querySelector('.site-header') || document.querySelector('.header');
      if (!header) return;
      var rect = header.getBoundingClientRect();
      this.overlay.style.setProperty('--mm-panel-top', rect.bottom + 'px');
      this.overlay.style.top = rect.bottom + 'px';
    }
  }

  /**
   * MegaMenuController - wires up all triggers on the page
   */
  class MegaMenuController {
    constructor() {
      this.activeDisclosure = null;
      this.settings = {
        hoverDelay: 200,
        closeOnScroll: true,
        headerSolidOnOpen: true,
        triggerMode: 'hover'
      };

      // Read settings from first overlay's data attributes
      var firstOverlay = document.querySelector('.mm-overlay');
      if (firstOverlay) {
        this.settings.hoverDelay = parseInt(firstOverlay.getAttribute('data-hover-delay')) || 200;
        this.settings.closeOnScroll = firstOverlay.getAttribute('data-close-on-scroll') !== 'false';
        this.settings.headerSolidOnOpen = firstOverlay.getAttribute('data-header-solid-on-open') !== 'false';
        this.settings.triggerMode = firstOverlay.getAttribute('data-trigger-mode') || 'hover';
      }

      this.init();
    }

    init() {
      var self = this;

      // Re-read settings from first overlay's data attributes (re-check in editor mode)
      var firstOverlay = document.querySelector('.mm-overlay');
      if (firstOverlay) {
        this.settings.hoverDelay = parseInt(firstOverlay.getAttribute('data-hover-delay')) || 200;
        this.settings.closeOnScroll = firstOverlay.getAttribute('data-close-on-scroll') !== 'false';
        this.settings.headerSolidOnOpen = firstOverlay.getAttribute('data-header-solid-on-open') !== 'false';
        this.settings.triggerMode = firstOverlay.getAttribute('data-trigger-mode') || 'hover';
      }

      var triggers = document.querySelectorAll('[data-mega-menu-trigger]');

      triggers.forEach(function (trigger) {
        if (trigger.hasAttribute('data-mega-menu-initialized')) return;

        var label = trigger.getAttribute('data-mega-menu-trigger');
        // Try to find the overlay by data-mega-menu-label attribute
        var overlay = document.querySelector('.mm-overlay[data-mega-menu-label="' + label + '"]');
        if (!overlay) {
          // Fallback: overlay might be a sibling of the trigger's parent
          overlay = trigger.parentElement.querySelector('.mm-overlay');
        }
        if (overlay) {
          trigger.setAttribute('data-mega-menu-initialized', 'true');
          new MegaMenuDisclosure(trigger, overlay, self.settings);
        }
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.MegaMenuController = new MegaMenuController();
    });
  } else {
    window.MegaMenuController = new MegaMenuController();
  }

  // Listen for Shopify Theme Editor events to re-initialize mega menu triggers when header is updated
  document.addEventListener('shopify:section:load', function (event) {
    var sectionType = event.target.getAttribute('data-section-type') || 
                      (event.target.firstElementChild && event.target.firstElementChild.getAttribute('data-section-type'));
    if (sectionType === 'header' || event.detail.sectionId === 'header') {
      if (window.MegaMenuController && typeof window.MegaMenuController.init === 'function') {
        window.MegaMenuController.init();
      }
    }
  });

})();